import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import DateRangePicker from '../components/DateRangePicker';
import InfoPanel from '../components/InfoPanel';
import { useDateRange } from '../hooks/useDateRange';
import { toCsv, downloadCsv, rangeSuffix } from '../utils/csv';

export default function BookViews() {
  const { fromDate, toDate, setFromDate, setToDate, from, to } = useDateRange();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: allBooks, isLoading } = useQuery({
    queryKey: ['allBooks', from, to],
    queryFn: () => api.getAllBooks(from, to),
  });

  const filteredBooks = useMemo(() => {
    if (!allBooks?.books) return [];
    if (!searchTerm) return allBooks.books;
    return allBooks.books.filter((book) =>
      book.book.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allBooks, searchTerm]);

  // Channel totals are taken across all books, not just the filtered set, so
  // that the app apportionment share stays stable while searching.
  const totals = useMemo(() => {
    const books = allBooks?.books ?? [];
    return books.reduce(
      (acc, b) => ({
        views: acc.views + Number(b.view_count),
        app: acc.app + Number(b.app_view_count),
        web: acc.web + Number(b.web_view_count),
        compass: acc.compass + Number(b.compass_view_count),
        unattributed: acc.unattributed + Number(b.unattributed_view_count),
      }),
      { views: 0, app: 0, web: 0, compass: 0, unattributed: 0 }
    );
  }, [allBooks]);

  // Only surface these columns when the selected range actually contains such
  // events, to keep the billing columns readable.
  const showCompass = totals.compass > 0;
  const showUnattributed = totals.unattributed > 0;

  const num = (v: number | string) => Number(v).toLocaleString();

  // Exports what is on screen, so a search-filtered view exports that subset.
  const handleExport = () => {
    const headers = [
      'Book',
      'Web impressions (billable)',
      'Web unique users',
      'App views',
      'App share %',
      'App unique users',
      'Compass views',
      'Views with no channel',
      'Total views',
    ];
    const rows = filteredBooks.map((b) => [
      b.book,
      Number(b.web_view_count),
      Number(b.web_unique_users),
      Number(b.app_view_count),
      totals.app > 0 ? ((Number(b.app_view_count) / totals.app) * 100).toFixed(2) : '',
      Number(b.app_unique_users),
      Number(b.compass_view_count),
      Number(b.unattributed_view_count),
      Number(b.view_count),
    ]);
    downloadCsv(
      `imray-books-by-channel_${rangeSuffix(fromDate, toDate)}.csv`,
      toCsv(headers, rows)
    );
  };

  return (
    <div className="space-y-6">
      <InfoPanel id="books" title="Books Overview">
        All books ranked by content views, split by billing channel. <strong>Web</strong> is charged
        per impression, so the web impression count is the billable figure. <strong>App</strong> is
        charged per subscriber, so app views are not a charge in themselves &mdash; the app share
        column shows each book&rsquo;s proportion of app reading, for apportioning subscriber revenue.
      </InfoPanel>

      <div className="flex justify-between items-start">
        <h1 className="text-2xl font-bold text-gray-900">Books by Channel</h1>
        <DateRangePicker
          from={fromDate}
          to={toDate}
          onFromChange={setFromDate}
          onToChange={setToDate}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card">
          <div className="text-sm text-gray-500">Web impressions (billable)</div>
          <div className="text-2xl font-bold text-gray-900">{num(totals.web)}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">App views (apportionment)</div>
          <div className="text-2xl font-bold text-gray-900">{num(totals.app)}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Total views</div>
          <div className="text-2xl font-bold text-gray-900">{num(totals.views)}</div>
        </div>
      </div>

      {showUnattributed && (
        <div className="card border-l-4 border-amber-400 bg-amber-50">
          <div className="text-sm text-amber-900">
            <strong>{num(totals.unattributed)}</strong> views in this range have no channel recorded
            and are excluded from the web and app columns. Device tracking began on 2 December 2024;
            earlier events cannot be attributed to web or app. Do not invoice from a range that
            includes them without accounting for this.
          </div>
        </div>
      )}

      <div className="card">
        <div className="mb-4 flex items-center justify-between gap-4">
          <input
            type="text"
            placeholder="Search books..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input max-w-md"
          />
          <button
            type="button"
            onClick={handleExport}
            disabled={filteredBooks.length === 0}
            className="px-4 py-2 text-sm font-medium rounded-md bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Export CSV
          </button>
        </div>
        <div className="text-sm text-gray-500 mb-2">
          Total: {num(totals.views)} views from {allBooks?.books.length || 0} books
        </div>
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="animate-pulse h-8 bg-gray-200 rounded"></div>
            ))}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Book</th>
                  <th className="text-right">Web impressions</th>
                  <th className="text-right">Web users</th>
                  <th className="text-right">App views</th>
                  <th className="text-right">App share</th>
                  <th className="text-right">App users</th>
                  {showCompass && <th className="text-right">Compass</th>}
                  {showUnattributed && <th className="text-right">No channel</th>}
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredBooks.map((book, index) => (
                  <tr key={index}>
                    <td className="text-gray-500">{index + 1}</td>
                    <td>{book.book}</td>
                    <td className="text-right font-medium">{num(book.web_view_count)}</td>
                    <td className="text-right text-gray-500">{num(book.web_unique_users)}</td>
                    <td className="text-right font-medium">{num(book.app_view_count)}</td>
                    <td className="text-right text-gray-500">
                      {totals.app > 0
                        ? `${((Number(book.app_view_count) / totals.app) * 100).toFixed(1)}%`
                        : '—'}
                    </td>
                    <td className="text-right text-gray-500">{num(book.app_unique_users)}</td>
                    {showCompass && (
                      <td className="text-right text-gray-500">{num(book.compass_view_count)}</td>
                    )}
                    {showUnattributed && (
                      <td className="text-right text-gray-500">
                        {num(book.unattributed_view_count)}
                      </td>
                    )}
                    <td className="text-right font-medium">{num(book.view_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
